import React, { useState, forwardRef, useMemo, useEffect } from 'react';
import { useTextField } from '@react-aria/textfield';
import { WrapLabel, HintWrapper, StyledTextareaInput, CounterSpan } from './styled';
import { FormLabel } from '../form-label';
import { FormHelperText } from '../form-helper-text';
import { useObjectRef } from '../../hooks/useObjectRef';
/**
 * @typedef {Object} TextareaInputPropsT
 * @extends IHintExtentionProps
 * @extends IFormLabelExtentionProps
 * @extends IFormHelperExtensionProps
 * @property {string} value - The value of the textarea input.
 * @property {(value: string) => void} onChange - Handler called when the textarea value changes.
 * @property {(e: FocusEvent<Element, Element>) => void} [onBlur] - Handler called when the textarea loses focus.
 * @property {(e: FocusEvent<Element, Element>) => void} [onFocus] - Handler called when the textarea gains focus.
 * @property {string} [name] - The name attribute for the textarea.
 * @property {ComponentSize} [size] - The size of the textarea, default is 'medium'.
 * @property {string} [placeholder] - The placeholder text for the textarea.
 * @property {boolean} [disabled] - Whether the textarea is disabled.
 * @property {number} [minLength] - The minimum length of the textarea value.
 * @property {number} [maxLength] - The maximum length of the textarea value.
 * @property {string} [ariaLabel] - The aria-label attribute for the textarea.
 * @property {number} [rows] - The number of rows for the textarea.
 * @property {number} [cols] - The number of columns for the textarea.
 * @property {string} [className] - Additional class names for the textarea.
 * @property {MessageInfoTypeT} [textType] - The type of text message for validation.
 * @property {boolean} [hasConsistentBorder] - Whether the textarea has a consistent border.
 * @property {boolean} [required] - Whether the textarea is a required field.
 * @property {ResizeOptions} [resize] - Behavior of text area resize, default is 'vertical'
 */
export const Textarea = forwardRef(function Textarea(props, externalRef) {
    const { onChange, value, size = 'medium', label, labelTooltip, hint, hintType, textType, minLength, ariaLabel, maxLength, className, disabled, fixedPadding, onBlur, onFocus, rows, cols, hasConsistentBorder = false, required, resize = 'vertical' } = props;
    const [isTouched, setIsTouched] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const [lengthMsg, setLengthMsg] = useState();
    const ref = useObjectRef(externalRef);
    const { labelProps, inputProps } = useTextField({ ...props, inputElementType: 'textarea' }, ref);
    const hasValidLength = useMemo(() => {
        if (minLength && !maxLength) {
            return Boolean(value.length >= minLength);
        }
        if (!minLength && maxLength) {
            return Boolean(value.length <= maxLength);
        }
        if (minLength && maxLength) {
            return Boolean(value.length >= minLength && value.length <= maxLength);
        }
        return true;
    }, [minLength, maxLength, value.length]);
    const hintTypeCalc = useMemo(() => {
        if (!hasValidLength) {
            return 'error';
        }
        else if (maxLength || minLength) {
            return 'success';
        }
        else {
            return hintType;
        }
    }, [hasValidLength, hintType]);
    const textTypeCalc = useMemo(() => {
        if (!hasValidLength) {
            return 'error';
        }
        else {
            return textType;
        }
    }, [hasValidLength, textType]);
    const hintMsgCalc = useMemo(() => {
        return lengthMsg || hint;
    }, [hint, lengthMsg]);
    useEffect(() => {
        if (isTouched && !hasValidLength && minLength && value.length < minLength) {
            setLengthMsg(`Must be at least ${minLength} characters`);
        }
        else if (isTouched && !hasValidLength && maxLength && value.length > maxLength) {
            setLengthMsg(`Maximum ${maxLength} characters`);
        }
        else {
            setLengthMsg(undefined);
        }
    }, [hasValidLength, minLength, maxLength, value.length, isTouched]);
    const renderTopDescription = () => {
        let message;
        if (value.length) {
            message = `${value.length}`;
        }
        else if (minLength) {
            message = `min. ${minLength} characters`;
        }
        else if (maxLength) {
            message = `max. ${maxLength} characters`;
        }
        const span = (React.createElement(CounterSpan, { "$isValid": hasValidLength, "$isTouched": isTouched, "$inputLength": value.length },
            message,
            ' '));
        let maxLengthCounter;
        const hasMaxLength = maxLength && value.length > 0;
        const meetsMinLength = !minLength || value.length >= minLength;
        if (hasMaxLength && meetsMinLength) {
            maxLengthCounter = `/ ${maxLength}`;
        }
        return (React.createElement(HintWrapper, { "$isValid": hasValidLength, "$isTouched": isTouched },
            message && span,
            " ",
            maxLengthCounter));
    };
    const handleBlur = (event) => {
        if (onBlur) {
            onBlur(event);
        }
        setIsTouched(true);
        setHasFocus(false);
    };
    const handleFocus = (e) => {
        if (onFocus) {
            onFocus(e);
        }
        setHasFocus(true);
    };
    const handleChange = (event) => {
        onChange(event.target.value);
        const textarea = ref.current;
        if (!textarea)
            return;
        if (textarea.style.height !== `${textarea.scrollHeight}px`) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    };
    return (React.createElement("div", null,
        React.createElement(WrapLabel, null,
            label && (React.createElement(FormLabel, { labelTooltip: labelTooltip, ...labelProps, required: required }, label)),
            (minLength || maxLength) && renderTopDescription()),
        React.createElement(StyledTextareaInput, { ...inputProps, ref: ref, rows: rows, cols: cols, disabled: disabled, "aria-label": ariaLabel, onFocus: handleFocus, onChange: handleChange, onBlur: handleBlur, "$hintType": hintTypeCalc, "$textType": textTypeCalc, "aria-invalid": hintTypeCalc === 'error', "$isTouched": hasFocus, "$sizeTextarea": size, "$hasConsistentBorder": hasConsistentBorder, className: className, required: required, "$inputLength": value.length, "$resize": resize }),
        React.createElement(FormHelperText, { fixedPadding: fixedPadding, msgType: hintTypeCalc }, hintMsgCalc)));
});
